import React from 'react'
import { useTranslation } from 'react-i18next'

import { env } from '@/config/env'
import { isStaffToken } from '@/utils/jwt'

import styles from './auth.module.css'
import { AuthContext } from './AuthContext'
import { AuthEvent, getInitPromise } from './authInitState'
import {
  clearCurrentRealm,
  getCurrentRealm,
  keycloakAuth,
  setDevelopmentGateBypassAuthToken,
} from './keycloak'

type AuthState =
  | { status: 'loading' }
  | { status: 'needs-realm' }
  | { status: 'authenticated'; token: string | null }
  | { status: 'error'; error: string }

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation()
  const [state, setState] = React.useState<AuthState>({ status: 'loading' })

  React.useEffect(() => {
    const handler = () => setState({ status: 'error', error: 'USER_DISABLED' })
    window.addEventListener(AuthEvent.UserDisabled, handler)
    return () => window.removeEventListener(AuthEvent.UserDisabled, handler)
  }, [])

  React.useEffect(() => {
    if (env.developmentGateBypassAuth) {
      setDevelopmentGateBypassAuthToken()
      setState({ status: 'authenticated', token: keycloakAuth.token })
      return () => {}
    }

    if (!getCurrentRealm()) {
      setState({ status: 'needs-realm' })
      return () => {}
    }

    let cancelled = false

    getInitPromise(keycloakAuth)
      .then(() => {
        if (cancelled) return
        if (keycloakAuth.isAuthenticated && keycloakAuth.token) {
          setState({
            status: 'authenticated',
            token: keycloakAuth.token,
          })
          keycloakAuth.setTokenChangeListener(() => {
            if (!cancelled) {
              setState(prev => {
                if (prev.status !== 'authenticated') return prev
                if (!keycloakAuth.isAuthenticated || !keycloakAuth.token) {
                  return { status: 'error', error: 'NOT_AUTHENTICATED' }
                }
                return { ...prev, token: keycloakAuth.token }
              })
            }
          })
        } else {
          setState({ status: 'error', error: 'NOT_AUTHENTICATED' })
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err)
          // System-level misconfiguration: show error screen (nothing the user can do).
          // All other failures (stale session, auth timeout, Keycloak rejection): clear
          // the stale realm from sessionStorage and show the realm picker so the user
          // can try again.
          if (message === 'KEYCLOAK_CONFIG_MISSING') {
            setState({ status: 'error', error: message })
          } else {
            clearCurrentRealm()
            setState({ status: 'needs-realm' })
          }
        }
      })

    return () => {
      cancelled = true
      keycloakAuth.setTokenChangeListener(null)
    }
  }, [])

  if (state.status === 'loading') {
    return <div className={styles.authScreen}>{t('AUTH_LOADING')}</div>
  }

  if (state.status === 'needs-realm') {
    return (
      <div className={styles.authErrorContainer}>
        <form
          className={styles.realmForm}
          onSubmit={e => {
            e.preventDefault()
            const input = (
              e.currentTarget.elements.namedItem('realm') as HTMLInputElement
            ).value.trim()
            if (input)
              window.location.assign(`/?realm=${encodeURIComponent(input)}`)
          }}
        >
          <p className={styles.realmTitle}>{t('AUTH_REALM_TITLE')}</p>
          <input
            className={styles.realmInput}
            name='realm'
            type='text'
            placeholder={t('AUTH_REALM_PLACEHOLDER')}
            autoFocus
            autoCapitalize='none'
            autoCorrect='off'
            spellCheck={false}
          />
          <button className={styles.realmButton} type='submit'>
            {t('AUTH_REALM_SUBMIT')}
          </button>
        </form>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className={styles.authErrorContainer}>
        <div className={styles.authErrorContent}>
          <p className={styles.authErrorTitle}>{t('AUTH_FAILED')}</p>
          <p className={styles.authErrorDetail}>
            {t(`AUTH_ERROR_${state.error}`, { defaultValue: state.error })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: state.status === 'authenticated',
        token: state.token,
        isStaff:
          env.developmentGateBypassAuth ||
          (state.status === 'authenticated' &&
            state.token !== null &&
            isStaffToken(state.token, env.authRealm, env.keycloakUrl)),
        logout: () => keycloakAuth.logout(),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
